import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { admin_bids_list } from "../../reduxData/user/userAction";
import CustomPagination from "../../components/CustomPagination";
import { Countdown, StatusBadge, Thumb, formatDate, imgUrl, money, pick, reveal } from "./shared";

const Info = ({ k, v }) => (
  <div className="la-field">
    <div className="k">{k}</div>
    <div className="v">{v === null || v === undefined || v === "" ? "-" : v}</div>
  </div>
);

const AuctionBids = ({ auctionId }) => {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBids = async () => {
      setLoading(true);
      try {
        const res = await admin_bids_list({ auction_id: auctionId, page, limit });
        const body = res?.data;
        const list = body?.data?.bids || body?.data || [];
        setRecords(Array.isArray(list) ? list : []);
        setTotal(body?.pagination?.total_records ?? body?.total ?? body?.data?.total ?? 0);
      } finally {
        setLoading(false);
      }
    };
    fetchBids();
  }, [auctionId, page, limit]);

  return (
    <div className="la-card mb-3 la-reveal" style={reveal(9)}>
      <div className="la-section-title">
        <i className="bi bi-cash-coin"></i>
        <span>Bids</span>
        <span className="la-sub ms-auto">{total} total</span>
      </div>
      {loading ? (
        <div className="p-3 d-grid gap-3">
          {[0, 1, 2].map((n) => <div key={n} className="s-line" style={{ height: 44 }} />)}
        </div>
      ) : records.length > 0 ? (
        <div className="la-ladder">
          {records.map((b, idx) => {
            const id = b?._id || b?.id;
            const rank = (page - 1) * limit + idx + 1;
            const sum = ["amount", "shipping_fee", "import_duty", "custom_duty"].reduce(
              (t, k) => t + (Number(b?.[k]) || 0),
              0
            );
            return (
              <button
                key={id || idx}
                className={`la-bidrow la-reveal ${rank === 1 ? "top" : ""}`}
                style={reveal(idx)}
                onClick={() => navigate(`/buyer-bids/${id}`)}
              >
                <span className="la-rank">{rank}</span>
                <div className="col">
                  <div className="la-title">{pick(b, "bidder_name", "bidder_email") || "-"}</div>
                  <div className="la-sub">{b?.bidder_name ? `${b.bidder_email} · ` : ""}User #{b?.user_id}</div>
                </div>
                <div className="col hide-sm">
                  <StatusBadge status={b?.bid_status} />
                  {b?.winner_user_id && b.winner_user_id === b.user_id && <span className="la-chip win ms-1">Winner</span>}
                </div>
                <div className="col">
                  <div className="k">Bid</div>
                  <div className="v">{money(b?.amount)}</div>
                </div>
                <div className="col hide-sm">
                  <div className="k">Total · {formatDate(b?.created_at)}</div>
                  <div className="v">{money(sum)}</div>
                </div>
                <i className="bi bi-chevron-right go"></i>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="la-empty"><i className="bi bi-inbox"></i>No bids yet</div>
      )}
      {total > limit && (
        <div className="p-3">
          <CustomPagination
            total={total}
            currentPage={page}
            perPage={limit}
            onPageChange={(currentPage, perPage) => {
              setPage(currentPage);
              setLimit(perPage);
            }}
          />
        </div>
      )}
    </div>
  );
};

const AuctionDetailView = ({ detail, id }) => {
  const navigate = useNavigate();
  const images = Array.isArray(detail?.images) ? detail.images : [];
  const [active, setActive] = useState(0);
  const dims = [detail?.length, detail?.width, detail?.height].every((d) => d)
    ? `${Number(detail.length)} × ${Number(detail.width)} × ${Number(detail.height)}`
    : null;

  const prices = [
    ["Starting Price", money(detail?.starting_price), ""],
    ["Current Price", money(detail?.current_price), "highlight"],
    ["Bid Increment", money(detail?.bid_increment), ""],
    ["Reserve Price", money(detail?.reserve_price), ""],
    ["Buy Now", money(detail?.buy_now_price), ""],
    ["Total Bids", detail?.bid_count ?? 0, ""],
  ];

  return (
    <>
      <div className="la-card la-hero la-hero-top la-reveal">
        <div className="la-gallery">
          {images.length ? (
            <div className="la-main-wrap"><img key={active} className="la-main-img" alt="" src={imgUrl(images[active])} /></div>
          ) : (
            <div className="la-main-wrap"><i className="bi bi-image"></i></div>
          )}
          {images.length > 1 && (
            <div className="la-thumbs">
              {images.map((im, i) => (
                <button key={im} className={`la-thumb-btn ${i === active ? "active" : ""}`} onClick={() => setActive(i)}>
                  <Thumb src={im} />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex-grow-1">
          <div className="la-sub mb-1">Auction #{id}</div>
          <h3 className="mb-2">{detail?.product_name || "-"}</h3>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <StatusBadge status={detail?.status} />
            {detail?.condition && <span className="la-chip">{detail.condition}</span>}
            {detail?.size && <span className="la-chip">{detail.size}</span>}
            {detail?.category_name && <span className="la-chip">{detail.category_name}</span>}
          </div>
          <p className="la-desc">{detail?.description || "No description provided."}</p>
          <div className="la-seller">
            <div className="la-avatar">
              {detail?.seller_pic ? (
                <img alt="" src={imgUrl(detail.seller_pic)} />
              ) : (
                <i className="bi bi-person"></i>
              )}
            </div>
            <div>
              <div className="la-sub">Seller</div>
              <div className="la-title">{detail?.seller_name || detail?.seller_email || "-"}</div>
              {detail?.seller_name && <div className="la-sub">{detail?.seller_email}</div>}
            </div>
          </div>
        </div>
      </div>

      {detail?.end_at && (
        <div className={`la-timer la-reveal ${["live", "active"].includes(String(detail?.status).toLowerCase()) ? "" : "ended"}`} style={reveal(1)}>
          <div>
            <div className="k">{["live", "active"].includes(String(detail?.status).toLowerCase()) ? "Time remaining" : "Auction ended"}</div>
            <div className="v">
              {["live", "active"].includes(String(detail?.status).toLowerCase()) ? <Countdown end={detail.end_at} /> : formatDate(detail.closed_at || detail.end_at)}
            </div>
          </div>
          <button className="btn btn-light btn-sm fw-bold" onClick={() => navigate(`/buyer-bids?auction_id=${id}`)}>
            <i className="bi bi-cash-coin me-1"></i>All bids
          </button>
        </div>
      )}

      <div className="la-stats">
        {prices.map(([k, v, cls], n) => (
          <div key={k} className={`la-card la-stat la-reveal ${cls}`} style={reveal(n + 2)}>
            <div className="k">{k}</div>
            <div className="v">{v}</div>
          </div>
        ))}
      </div>

      <div className="la-two-col la-reveal" style={reveal(6)}>
        <div className="la-card">
          <div className="la-section-title"><i className="bi bi-calendar-event"></i>Schedule</div>
          <div className="la-fields">
            <Info k="Starts" v={formatDate(detail?.start_at)} />
            <Info k="Ends" v={formatDate(detail?.end_at)} />
            <Info k="Created" v={formatDate(detail?.created_at)} />
            <Info k="Closed" v={detail?.closed_at ? formatDate(detail.closed_at) : null} />
          </div>
        </div>
        <div className="la-card">
          <div className="la-section-title"><i className="bi bi-box-seam"></i>Product</div>
          <div className="la-fields">
            <Info k="Weight" v={detail?.weight ? Number(detail.weight) : null} />
            <Info k="Dimensions (L × W × H)" v={dims} />
            <Info k="Condition" v={detail?.condition} />
            <Info k="Size" v={detail?.size} />
          </div>
        </div>
      </div>

      <div className="la-card mb-3 la-reveal" style={reveal(8)}>
        <div className="la-section-title"><i className="bi bi-trophy"></i>Result</div>
        <div className="la-fields">
          <Info k="Winner" v={detail?.winner_user_id ? `User #${detail.winner_user_id}` : null} />
          <Info k="Winning Amount" v={detail?.winning_amount != null ? money(detail.winning_amount) : null} />
          <Info k="Sold Via" v={detail?.sold_via} />
          <Info
            k="Order"
            v={
              detail?.order_id ? (
                <button className="btn btn-link p-0" onClick={() => navigate(`/view-orders/${detail.order_id}`)}>
                  #{detail.order_id}
                </button>
              ) : null
            }
          />
        </div>
      </div>

      <AuctionBids auctionId={id} />
    </>
  );
};

export default AuctionDetailView;
